import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AdminDiagnostic = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const addResult = (step, status, details) => {
    setResults(prev => [...prev, { step, status, details, time: new Date().toLocaleTimeString() }]);
  };

  const runDiagnostics = async () => {
    setResults([]);
    setLoading(true);

    // Test 1: Check Supabase connection
    try {
      const { data, error } = await supabase.from('admins').select('count');
      if (error) throw error;
      addResult('Supabase Connection', '✅ OK', 'Connected to database');
    } catch (err) {
      addResult('Supabase Connection', '❌ FAIL', err.message);
      setLoading(false);
      return;
    }

    // Test 2: Check if jogijacob89@gmail.com exists in admins table
    try {
      const { data, error } = await supabase
        .from('admins')
        .select('*')
        .eq('email', 'jogijacob89@gmail.com')
        .maybeSingle();
      
      if (error) throw error;
      
      if (data) {
        addResult('Admin Record', '✅ FOUND', `Role: ${data.role}, Active: ${data.is_active}`);
      } else {
        addResult('Admin Record', '❌ MISSING', 'No admin record found for jogijacob89@gmail.com');
      }
    } catch (err) {
      addResult('Admin Record', '❌ ERROR', err.message);
    }

    // Test 3: Check all admins (without email filter - tests RLS)
    try {
      const { data, error } = await supabase
        .from('admins')
        .select('email, role, is_active')
        .limit(10);
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        addResult('All Admins Query', '✅ OK', `Found ${data.length} admin(s): ${data.map(a => a.email).join(', ')}`);
      } else {
        addResult('All Admins Query', '⚠️ EMPTY', 'No admins found in database');
      }
    } catch (err) {
      addResult('All Admins Query', '❌ RLS BLOCKED', err.message);
    }

    // Test 4: Check current auth session
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      
      if (session?.user) {
        addResult('Auth Session', '✅ LOGGED IN', `Email: ${session.user.email}`);
      } else {
        addResult('Auth Session', '⚠️ NO SESSION', 'No active session');
      }
    } catch (err) {
      addResult('Auth Session', '❌ ERROR', err.message);
    }

    setLoading(false);
  };

  // Auto-run on mount
  useEffect(() => {
    runDiagnostics();
  }, []);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-xl font-bold mb-4">Admin Login Diagnostics</h2>
        
        <button
          onClick={runDiagnostics}
          disabled={loading}
          className="mb-6 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          {loading ? 'Running...' : 'Re-run Diagnostics'}
        </button>

        <div className="space-y-3">
          {results.map((r, i) => (
            <div key={i} className={`p-4 rounded-lg border ${
              r.status.includes('✅') ? 'bg-green-50 border-green-200' :
              r.status.includes('❌') ? 'bg-red-50 border-red-200' :
              'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">{r.status}</span>
                <span className="font-semibold">{r.step}</span>
                <span className="text-xs text-gray-400 ml-auto">{r.time}</span>
              </div>
              <p className="text-sm text-gray-700">{r.details}</p>
            </div>
          ))}
        </div>

        {results.length > 0 && results.some(r => r.status.includes('❌')) && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-800 mb-2">Fix Instructions:</h3>
            <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
              <li>Go to Supabase Dashboard → SQL Editor</li>
              <li>Run the <code>fix-admins-rls.sql</code> file first</li>
              <li>Run the <code>check-and-create-admin.sql</code> file</li>
              <li>If "Admin Record" is missing, ensure the email exists in Authentication → Users</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDiagnostic;
